import React, { useEffect, useState } from "react";
import { useFormikContext } from "formik";
import { Box, FormHelperText, formHelperTextClasses, MenuItem, Stack } from "@mui/material";
import * as yup from "yup";
import {
    alphaSpaceApostropheHyphensOnly,
    digitsOnly,
    RoadsideFieldLabel,
    RoadsideTextField,
} from "components/FormElements/FormElements.styled";
import { SalutationField } from "components/SalutationField";
import { InferType } from "yup";
import { Responsive } from "components/ThemeWrapper";
import { differenceInYears, isFuture, isLeapYear, parseISO } from "date-fns";
import { TitleState } from "state";
import { capitalize } from "utils";

const genders = ["Male", "Female", "Unspecified"];

export const schemaShape = {
    personalDetailsForm: yup.object({
        title: yup.string().required("Please select a title."),
        isLifestyle: yup.boolean().required(),
        givenNames: yup.string().trim().required("Please enter your given names."),
        surname: yup.string().trim().required("Please enter your surname."),
        gender: yup.mixed<string>().oneOf(genders).required("Please select your gender."),
        dateOfBirth: yup
            .object({
                day: yup.string(),
                month: yup.string(),
                year: yup.string(),
            })
            .test("dateOfBirth", "Date of birth validation error", (value, context) => {
                if (!value.year && !value.month && !value.day) {
                    return context.createError({ message: "Please enter your date of birth." });
                }

                if ((!value.year ? 1 : 0) + (!value.month ? 1 : 0) + (!value.day ? 1 : 0) >= 2) {
                    return context.createError({ message: "Please complete your date of birth." });
                }

                if (!value.day) {
                    return context.createError({
                        path: "personalDetailsForm.dateOfBirth.day",
                        message: "Please enter your birth day.",
                    });
                }

                if (!value.month) {
                    return context.createError({
                        path: "personalDetailsForm.dateOfBirth.month",
                        message: "Please enter your birth month.",
                    });
                }

                if (!value.year) {
                    return context.createError({
                        path: "personalDetailsForm.dateOfBirth.year",
                        message: "Please enter your birth year.",
                    });
                }

                const daysInMonth = [
                    31,
                    value.year && isLeapYear(parseISO(`${value.year}-06-01`)) ? 29 : 28,
                    31,
                    30,
                    31,
                    30,
                    31,
                    31,
                    30,
                    31,
                    30,
                    31,
                ];
                const dayAsInt = parseInt(value.day, 10);
                const monthAsInt = parseInt(value.month, 10);
                const yearAsInt = parseInt(value.year, 10);
                const maxDay = value.month ? daysInMonth[monthAsInt - 1] || 31 : 31;
                const dayOutOfRange =
                    isNaN(dayAsInt) || dayAsInt < 1 || dayAsInt > maxDay || !/^[0-9]{1,2}$/.test(value.day);
                const monthOutOfRange =
                    isNaN(monthAsInt) || monthAsInt < 1 || monthAsInt > 12 || !/^[0-9]{1,2}$/.test(value.month);
                const yearOutOfRange = isNaN(yearAsInt) || !/^[0-9]{4}$/.test(value.year);
                const date = parseISO(`${value.year}-${value.month.padStart(2, "0")}-${value.day.padStart(2, "0")}`);

                if (dayOutOfRange || monthOutOfRange || yearOutOfRange) {
                    return context.createError({
                        message: "Please enter a valid value for day/month/year.",
                    });
                }

                if (isFuture(date)) {
                    return context.createError({
                        message: "That date is in the future, please enter a valid date.",
                    });
                }

                if (differenceInYears(new Date(), date) < 16) {
                    return context.createError({
                        message: `Unfortunately you must be 16 years old to obtain a ${context.parent.isLifestyle ? "lifestyle" : "roadside"} product`,
                    });
                }

                if (differenceInYears(new Date(), date) > 100) {
                    return context.createError({
                        message: "Please check your year of birth is correct and try again.",
                    });
                }

                return true;
            }),
    }),
};

const schema = yup.object().shape(schemaShape);

type Values = InferType<typeof schema>;

const primaryTitles = ["Mr", "Mrs", "Miss", "Ms"];

export const PersonalDetailsForm: React.FC<{ titles?: TitleState[]; children?: React.ReactNode }> = ({ titles }) => {
    const { values, errors, touched, handleChange, handleBlur, setFieldValue } = useFormikContext<Values>();
    const [showGender, setShowGender] = useState(
        !titles?.find((t) => t.title === values.personalDetailsForm.title)?.genderSpecific,
    );
    const genderField = "personalDetailsForm.gender";
    const touchedDateOfBirth =
        touched.personalDetailsForm?.dateOfBirth?.day &&
        touched.personalDetailsForm?.dateOfBirth?.month &&
        touched.personalDetailsForm?.dateOfBirth?.year;

    useEffect(() => {
        const title = titles?.find((t) => t.title === values.personalDetailsForm.title);
        if (!!title?.genderSpecific) {
            setFieldValue(genderField, capitalize(title.gender!));
            setShowGender(false);
        } else if (!title) {
            setFieldValue(genderField, "");
            setShowGender(false);
        } else if (!showGender) {
            setFieldValue(genderField, "");
            setShowGender(true);
        }
    }, [setFieldValue, titles, values.personalDetailsForm.title, showGender, setShowGender]);

    return (
        <Box sx={{ width: "100%", maxWidth: "475px" }}>
            <RoadsideFieldLabel variant="subtitle2">Title</RoadsideFieldLabel>
            <SalutationField
                primarySalutations={(titles || primaryTitles.map((t) => ({ title: t })))
                    .filter((t) => primaryTitles.includes(t.title))
                    .map((t) => t.title)
                    .sort((a, b) => primaryTitles.indexOf(a) - primaryTitles.indexOf(b))}
                otherSalutations={(titles || [])
                    .filter((t) => !primaryTitles.includes(t.title))
                    .map((t) => t.title)
                    .sort((a, b) => a.localeCompare(b))}
                name={"personalDetailsForm.title"}
                value={values.personalDetailsForm.title}
                onChange={handleChange}
                error={touched.personalDetailsForm?.title && Boolean(errors.personalDetailsForm?.title)}
                helperText={touched.personalDetailsForm?.title && errors.personalDetailsForm?.title}
            />
            <RoadsideTextField
                variant="outlined"
                fullWidth
                name="personalDetailsForm.givenNames"
                onInput={alphaSpaceApostropheHyphensOnly}
                onChange={handleChange}
                onBlur={handleBlur}
                value={values.personalDetailsForm.givenNames}
                label="Given names"
                error={touched.personalDetailsForm?.givenNames && Boolean(errors.personalDetailsForm?.givenNames)}
                helperText={touched.personalDetailsForm?.givenNames && errors.personalDetailsForm?.givenNames}
                inputProps={{
                    spellCheck: "false",
                }}
            />
            <RoadsideTextField
                variant="outlined"
                fullWidth
                name="personalDetailsForm.surname"
                onInput={alphaSpaceApostropheHyphensOnly}
                onChange={handleChange}
                onBlur={handleBlur}
                value={values.personalDetailsForm.surname}
                label="Surname"
                error={touched.personalDetailsForm?.surname && Boolean(errors.personalDetailsForm?.surname)}
                helperText={touched.personalDetailsForm?.surname && errors.personalDetailsForm?.surname}
                inputProps={{
                    spellCheck: "false",
                }}
            />
            {showGender && (
                <RoadsideTextField
                    variant="outlined"
                    fullWidth
                    name={genderField}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    value={values.personalDetailsForm.gender}
                    label="Gender"
                    select
                    error={touched.personalDetailsForm?.gender && Boolean(errors.personalDetailsForm?.gender)}
                    helperText={String(touched.personalDetailsForm?.gender && errors.personalDetailsForm?.gender)}
                >
                    {genders.map((g) => (
                        <MenuItem key={g} value={g}>
                            {g}
                        </MenuItem>
                    ))}
                </RoadsideTextField>
            )}
            <RoadsideFieldLabel variant="subtitle2">Date of birth</RoadsideFieldLabel>
            <Stack direction={"column"} spacing={1}>
                <Stack direction={"row"} spacing={3}>
                    <RoadsideTextField
                        sx={{
                            mb: 0,
                            width: Responsive("25%", "15%"),
                            [`& .${formHelperTextClasses.root}`]: {
                                textAlign: "center",
                                color: "text.primary",
                                [`&.Mui-error`]: {
                                    color: "text.primary",
                                },
                            },
                        }}
                        variant="outlined"
                        name="personalDetailsForm.dateOfBirth.day"
                        onInput={digitsOnly}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        value={values.personalDetailsForm.dateOfBirth.day}
                        placeholder="DD"
                        helperText="Day"
                        inputProps={{
                            maxLength: 2,
                            inputMode: "numeric",
                            pattern: "[0-9]*",
                        }}
                        error={
                            touchedDateOfBirth &&
                            (typeof errors.personalDetailsForm?.dateOfBirth === "string" ||
                                Boolean(errors.personalDetailsForm?.dateOfBirth?.day))
                        }
                    />
                    <RoadsideTextField
                        sx={{
                            mb: 0,
                            width: Responsive("25%", "15%"),
                            [`& .${formHelperTextClasses.root}`]: {
                                textAlign: "center",
                                color: "text.primary",
                                [`&.Mui-error`]: {
                                    color: "text.primary",
                                },
                            },
                        }}
                        variant="outlined"
                        name="personalDetailsForm.dateOfBirth.month"
                        onInput={digitsOnly}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        value={values.personalDetailsForm.dateOfBirth.month}
                        placeholder="MM"
                        helperText="Month"
                        inputProps={{
                            maxLength: 2,
                            inputMode: "numeric",
                            pattern: "[0-9]*",
                        }}
                        error={
                            touchedDateOfBirth &&
                            (typeof errors.personalDetailsForm?.dateOfBirth === "string" ||
                                Boolean(errors.personalDetailsForm?.dateOfBirth?.month))
                        }
                    />
                    <RoadsideTextField
                        sx={{
                            mb: 0,
                            width: Responsive("50%", "40%"),
                            [`& .${formHelperTextClasses.root}`]: {
                                textAlign: "center",
                                color: "text.primary",
                                [`&.Mui-error`]: {
                                    color: "text.primary",
                                },
                            },
                        }}
                        variant="outlined"
                        name="personalDetailsForm.dateOfBirth.year"
                        onInput={digitsOnly}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        value={values.personalDetailsForm.dateOfBirth.year}
                        placeholder="YYYY"
                        helperText="Year"
                        inputProps={{
                            maxLength: 4,
                            inputMode: "numeric",
                            pattern: "[0-9]*",
                        }}
                        error={
                            touchedDateOfBirth &&
                            (typeof errors.personalDetailsForm?.dateOfBirth === "string" ||
                                Boolean(errors.personalDetailsForm?.dateOfBirth?.year))
                        }
                    />
                </Stack>
                <FormHelperText
                    hidden={!(touchedDateOfBirth && Boolean(errors.personalDetailsForm?.dateOfBirth))}
                    error={touchedDateOfBirth && Boolean(errors.personalDetailsForm?.dateOfBirth)}
                    sx={{ fontSize: 12, m: 0 }}
                >
                    {typeof errors.personalDetailsForm?.dateOfBirth === "object"
                        ? errors.personalDetailsForm?.dateOfBirth.day
                            ? errors.personalDetailsForm?.dateOfBirth.day
                            : errors.personalDetailsForm?.dateOfBirth.month
                              ? errors.personalDetailsForm?.dateOfBirth.month
                              : errors.personalDetailsForm?.dateOfBirth.year
                                ? errors.personalDetailsForm?.dateOfBirth.year
                                : "Error"
                        : errors.personalDetailsForm?.dateOfBirth}
                </FormHelperText>
            </Stack>
        </Box>
    );
};
